/* 前后端共享的类型写在这里 */

// ============ 文件附件类型 ============
export interface FileAttachment {
  bucket_id: string;
  file_path: string;
}

// ============ 演讲主题相关类型 ============

// 适用年级
export type SuitableGrade = 'high' | 'low';

// 内容方向（自有主题数据字段：中国精神、中国智慧等）
export type ContentDirection =
  | 'chinese_feature'     // 中国特色
  | 'chinese_spirit'      // 中国精神
  | 'chinese_wisdom'      // 中国智慧
  | 'other';              // 其他

// 内容分类
export type ContentCategory =
  | 'traditional_culture'   // 传统文化
  | 'idiom_fable'           // 成语寓言
  | 'patriotic_feeling'     // 爱国情怀
  | 'character_spirit'      // 人物精神
  | 'city_feature'          // 城市特色
  | 'myth_legend'           // 神话传说
  | 'classic_fairy_tale'    // 经典童话
  | 'picture_book_story';   // 绘本故事

// 演讲主题基础信息（列表展示用）
export interface SpeechTopic {
  id: string;
  title: string;
  cover_image: string | null;
  suitable_grade: SuitableGrade;
  content_category: ContentCategory;
  content_direction: ContentDirection;
  estimated_duration: string;
}

// 演讲主题详情（弹窗展示用）
export interface SpeechTopicDetail extends SpeechTopic {
  content_description: string;
  script_display: string | null;
  video_display: string | null;
}

export interface AdminSpeechTopic {
  id: string;
  title: string;
  cover_image: string | null;
  suitable_grade: SuitableGrade;
  content_category: ContentCategory;
  content_direction: ContentDirection;
  estimated_duration: string;
  content_description: string;
  script_display: string | null;
  core_value: string;
  video_display: string | null;
  ppt_display: string | null;
  download_link: string;
  view_count: number;
  redeem_count: number;
  sort_weight: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminSpeechTopicListResp {
  items: AdminSpeechTopic[];
  total: number;
}

// 演讲主题列表响应
export interface SpeechTopicListResp {
  items: SpeechTopic[];
  total: number;
}

// 获取演讲主题列表请求参数
export interface GetSpeechTopicListReq {
  search?: string;           // 搜索关键词（主题名称或文案）
  category?: ContentCategory; // 内容分类筛选
  grade?: SuitableGrade;     // 适用年级筛选
  page?: number;             // 页码，默认1
  pageSize?: number;         // 每页条数，默认20
}

// 创建演讲主题请求
export interface CreateSpeechTopicReq {
  title: string;
  cover_image?: string | null;
  suitable_grade: SuitableGrade;
  content_category: ContentCategory;
  content_direction: ContentDirection;
  estimated_duration: string;
  content_description: string;
  script_display?: string | null;
  core_value?: string;
  video_display?: string | null;
  ppt_display?: string | null;
  download_link: string;
  sort_weight?: number;
  is_active?: boolean;
}

// 创建演讲主题响应
export interface CreateSpeechTopicResp {
  id: string;
}

// 更新演讲主题请求
export interface UpdateSpeechTopicReq {
  title?: string;
  cover_image?: string | null;
  suitable_grade?: SuitableGrade;
  content_category?: ContentCategory;
  content_direction?: ContentDirection;
  estimated_duration?: string;
  content_description?: string;
  script_display?: string | null;
  core_value?: string;
  video_display?: string | null;
  ppt_display?: string | null;
  download_link?: string;
  sort_weight?: number;
  is_active?: boolean;
}

// 更新演讲主题响应
export interface UpdateSpeechTopicResp {
  id: string;
}

// 删除演讲主题响应
export interface DeleteSpeechTopicResp {
  success: boolean;
}

export interface ReorderSpeechTopicsReq {
  topic_ids: string[];
}

export interface ReorderSpeechTopicsResp {
  success: boolean;
}

export interface AdminSpeechTopicImportItem {
  title: string;
  cover_image?: string;
  suitable_grade: SuitableGrade;
  content_category: ContentCategory;
  content_direction: ContentDirection;
  estimated_duration: string;
  content_description: string;
  script_display?: string | null;
  core_value?: string;
  video_display?: string;
  ppt_display?: string;
  download_link: string;
  sort_weight?: number;
  is_active?: boolean;
}

export interface AdminSpeechTopicImportPreviewRow {
  row_number: number;
  topic_name: string;
  // 同名主题（数据库已存在或文件内重名）一律为 error，不存在“更新”路径
  action: 'create' | 'error';
  errors: string[];
  item?: AdminSpeechTopicImportItem;
}

export interface AdminSpeechTopicImportPreviewResp {
  rows: AdminSpeechTopicImportPreviewRow[];
  summary: {
    total: number;
    creatable: number;
    invalid: number;
  };
}

export interface AdminSpeechTopicImportCommitReq {
  items: AdminSpeechTopicImportItem[];
}

export interface AdminSpeechTopicImportCommitResp {
  success: boolean;
  created: number;
}

// ============ 预约相关类型 ============

// ============ 分类/标签显示配置 ============

export const CONTENT_CATEGORY_LABELS: Record<ContentCategory, string> = {
  traditional_culture: '传统文化',
  idiom_fable: '成语寓言',
  patriotic_feeling: '爱国情怀',
  character_spirit: '人物精神',
  city_feature: '城市特色',
  myth_legend: '神话传说',
  classic_fairy_tale: '经典童话',
  picture_book_story: '绘本故事',
};

export const SUITABLE_GRADE_LABELS: Record<SuitableGrade, string> = {
  high: '高年级',
  low: '低年级',
};

export const CONTENT_DIRECTION_LABELS: Record<ContentDirection, string> = {
  chinese_feature: '中国特色',
  chinese_spirit: '中国精神',
  chinese_wisdom: '中国智慧',
  other: '其他',
};

// 分类颜色配置（用于UI展示）
export const CONTENT_CATEGORY_COLORS: Record<ContentCategory, { bg: string; text: string }> = {
  traditional_culture: { bg: 'bg-red-400/20', text: 'text-red-700' },
  idiom_fable: { bg: 'bg-amber-400/20', text: 'text-amber-700' },
  patriotic_feeling: { bg: 'bg-red-500/20', text: 'text-red-800' },
  character_spirit: { bg: 'bg-sky-400/20', text: 'text-sky-700' },
  city_feature: { bg: 'bg-emerald-400/20', text: 'text-emerald-700' },
  myth_legend: { bg: 'bg-violet-400/20', text: 'text-violet-700' },
  classic_fairy_tale: { bg: 'bg-fuchsia-400/20', text: 'text-fuchsia-700' },
  picture_book_story: { bg: 'bg-orange-400/20', text: 'text-orange-700' },
};

export const SUITABLE_GRADE_COLORS: Record<SuitableGrade, { bg: string; text: string }> = {
  high: { bg: 'bg-blue-400/20', text: 'text-blue-700' },
  low: { bg: 'bg-green-400/20', text: 'text-green-700' },
};

export const CONTENT_DIRECTION_COLORS: Record<ContentDirection, { bg: string; text: string }> = {
  chinese_feature: { bg: 'bg-red-400/20', text: 'text-red-700' },
  chinese_spirit: { bg: 'bg-orange-400/20', text: 'text-orange-700' },
  chinese_wisdom: { bg: 'bg-amber-400/20', text: 'text-amber-700' },
  other: { bg: 'bg-slate-400/20', text: 'text-slate-700' },
};

// ============ 会员兑换相关类型 ============

// 会员类型（动态，从配置表读取）
export type MemberType = string;

// 会员状态
export type MemberStatus = 'active' | 'frozen' | 'expired';



// 会员类型配置
export interface MemberTypeConfig {
  id: string;                 // 记录ID
  name: string;               // 类型名称（如：轻享年卡、畅享年卡）
  monthly_quota: number;      // 月配额
  daily_limit: number;        // 单日上限
  validity_months: number;    // 有效期（月）
  status: '启用' | '停用';     // 状态
  quota_mode: 'monthly' | 'total';  // 配额模式: monthly=按月配额, total=按年度总次数
  total_quota: number;        // 年度总次数, 仅在quota_mode=total时有效
}

// 会员信息
export interface Member {
  passcode: string;           // 兑换码
  member_type: MemberType;    // 会员类型（类型名称）
  status: MemberStatus;       // 状态
  quota_mode?: 'monthly' | 'total'; // 配额模式
  total_quota?: number;       // 总次数上限（年度总次数模式）
  expiry_date: string;        // 到期日期
  monthly_quota: number;      // 月配额（从配置表读取）
  daily_limit: number;        // 单日上限（从配置表读取）
  cycle_used: number;         // 本周期已领
  today_used: number;         // 今日已领
  last_redemption_date: string; // 最后领取日期
  cycle_start_date: string;   // 本周期开始日
  start_date: string;         // 开通日期
  source_channel?: string;    // 来源渠道
  total_redemptions: number;  // 累计领取
  remark?: string;             // 备注
}

// 会员验证结果
export interface MemberVerifyResult {
  success: boolean;
  member?: Member;
  error_code?: import('./member-errors').MemberErrorCode;
  error_message?: string;
  remaining_cycle: number;    // 本周期剩余额度
  remaining_today: number;    // 今日剩余次数
}

// 验证兑换码请求
export interface VerifyPasscodeReq {
  passcode: string;
  topic_name?: string;
}

// 资源主题（展示用，从演讲主题表读取）
export interface ResourceTopic {
  id: string;
  topic_name: string;
  cover_image: string | null;
  content_category: ContentCategory;
  content_direction: ContentDirection;  // 内容方向（中国精神/中国智慧等）
  suitable_grade: SuitableGrade;
  estimated_duration: string;
  content_description: string;
  script_display: string | null;
  core_value: string;
  video_display: string | null;
  ppt_display: string | null;  // PPT预览文件
}

// 领取请求
export interface RedeemRequest {
  passcode: string;
  topic_name: string;
}

// 领取结果
export interface RedeemResult {
  success: boolean;
  topic_name: string;
  member_type: string;
  download_link: string;
  remaining_cycle: number;
  remaining_today: number;
  already_redeemed?: boolean;
  message?: string;
}

// 领取记录
export interface RedemptionRecord {
  id: string;
  passcode: string;
  topic_name: string;
  member_type: string;
  redemption_time: string;
  record_type: 'redemption' | 'quota_adjustment';
  result: 'success' | 'failed' | 'adjusted';
  fail_reason?: string;
  download_link?: string;
  adjustment_note?: string;
  quota_before?: MemberQuotaSnapshot;
  quota_after?: MemberQuotaSnapshot;
}

export interface MemberQuotaSnapshot {
  cycle_used: number;
  today_used: number;
  total_redemptions: number;
  last_redemption_date: string | null;
}

// 领取记录列表响应
export interface RedemptionRecordListResp {
  items: RedemptionRecord[];
  total: number;
}

// ============ 管理员后台类型 ============

// 管理员登录请求
export interface AdminLoginReq {
  password: string;
}

// 管理员登录响应
export interface AdminLoginResp {
  token: string;
}

// 客服承接配置
export type ContactMethod = 'qr' | 'link';

export interface ContactConfig {
  enabled: boolean;
  // 登录按钮旁独立展示开关：只控制“登录”旁的客服入口，不影响详情/兑换等现有入口
  show_header_entry: boolean;
  method: ContactMethod;
  title: string;
  description: string;
  detail_description: string;
  passcode_hint: string;
  button_text: string;
  wechat_id: string;
  qr_image_url: string;
  link_url: string;
}

export interface UpdateContactConfigReq extends ContactConfig {}

// 新增会员请求
export interface CreateMemberReq {
  passcode: string;
  member_type: string;  // 会员类型名称
  start_date: string;   // 开通日期
  source_channel?: string;  // 来源渠道
  remark?: string;      // 备注
}

// 更新会员状态请求
export interface UpdateMemberReq {
  status?: MemberStatus;
  cycle_used?: number; // 手动调整本周期已领数量
  today_used?: number; // 手动调整今日已领数量
  last_redemption_date?: string | null; // 手动调整最后领取日期
  total_redemptions?: number; // 手动调整累计领取数量
  source_channel?: string; // 来源渠道
  remark?: string; // 备注
  adjustment_note?: string; // 额度调整说明，最长500字
}

export interface UpdateMemberResp {
  adjustment_recorded: boolean;
}

// 会员列表响应
export interface MemberListResp {
  items: Member[];
  total: number;
}

// 获取会员列表请求
export interface GetMemberListReq {
  search?: string;
  page?: number;
  pageSize?: number;
}

// 获取领取记录请求
export interface GetRedemptionRecordsReq {
  passcode?: string;
  page?: number;
  pageSize?: number;
}

export interface MemberAdminDetailResp {
  member: Member;
  recent_records: RedemptionRecord[];
}



// 会员状态标签
export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  active: '启用',
  frozen: '冻结',
  expired: '已过期',
};



// ============ 会员类型配置相关类型 ============

// 获取会员类型配置列表响应
export interface MemberTypeConfigListResp {
  items: MemberTypeConfig[];
  total: number;
}

// 创建会员类型配置请求
export interface CreateMemberTypeConfigReq {
  name: string;              // 类型名称
  monthly_quota: number;     // 月配额
  daily_limit: number;       // 单日上限
  validity_months: number;   // 有效期（月）
  status?: '启用' | '停用';   // 状态，默认启用
  quota_mode?: 'monthly' | 'total';  // 配额模式，默认monthly
  total_quota?: number;      // 年度总次数
}

// 更新会员类型配置请求
export interface UpdateMemberTypeConfigReq {
  name?: string;
  monthly_quota?: number;
  daily_limit?: number;
  validity_months?: number;
  status?: '启用' | '停用';
  quota_mode?: 'monthly' | 'total';
  total_quota?: number;
}

// 创建会员类型配置响应
export interface CreateMemberTypeConfigResp {
  id: string;
}

// 删除会员类型配置响应
export interface DeleteMemberTypeConfigResp {
  success: boolean;
}

// ============ 管理员登录记录类型 ============

// 管理员登录历史记录
export interface AdminLoginHistoryRecord {
  id: string;
  client_ip: string;
  user_agent: string;
  login_status: 'success' | 'failed';
  fail_reason?: string;
  session_id?: string;
  created_at: string;
}

// 管理员登录历史列表响应
export interface AdminLoginHistoryListResp {
  items: AdminLoginHistoryRecord[];
  total: number;
}

// 管理员密码修改请求
export interface UpdateAdminPasswordReq {
  currentPassword: string;
  newPassword: string;
}

// 管理员密码配置响应
export interface AdminPasswordConfigResp {
  hasPassword: boolean;
}



